import { Internal } from '@gleamer/types';
import classNames from 'classnames';
import React from 'react';
import { DATE_LOCALE } from '../../../utils/locale';
import { TaskLink } from '../TaskLink';

type LabelTask = Internal.LabelTask;

/**
 * Finished for the user: a task they label with nothing left to claim and nothing held (viewer-api's progress,
 * gdata-backend #847). A task they review is never finished — each review is a session they start.
 */
export function isFinished(task: Pick<LabelTask, 'progress'>): boolean {
  return (
    !!task.progress &&
    task.progress.left === 0 &&
    task.progress.inProgress === 0
  );
}

/**
 * How far along the user is on a task they label: what they labelled over everything there is for them, an
 * item they hold counting as one left. Null on a task they review (no progress is given) or with nothing
 * for them.
 */
export function progressShare(
  task: Pick<LabelTask, 'progress'>
): number | null {
  const p = task.progress;
  if (!p) {
    return null;
  }
  const total = p.labeled + p.inProgress + p.left;
  return total > 0 ? p.labeled / total : null;
}

/**
 * Where a task comes in the "most advanced first" order: those started and not done, then those not
 * started, then the reviews, then those with nothing for the user, and the fully labelled last.
 */
function progressTier(task: Pick<LabelTask, 'progress'>): number {
  const p = task.progress;
  if (!p) return 2;
  const share = progressShare(task);
  if (share === null) return 3;
  if (share >= 1) return 4;
  return share > 0 ? 0 : 1;
}

/** Most advanced first, by tier then by share (labelled / total), newest first among equals. */
export function byProgress(
  a: Pick<LabelTask, 'progress' | 'createdAt'>,
  b: Pick<LabelTask, 'progress' | 'createdAt'>
): number {
  return (
    progressTier(a) - progressTier(b) ||
    (progressShare(b) ?? 0) - (progressShare(a) ?? 0) ||
    b.createdAt.localeCompare(a.createdAt)
  );
}

/** How far the user is on a task they label: a bar and its words. */
export function TaskProgress({
  task,
  className = 'mt-4',
}: {
  task: Pick<LabelTask, 'name' | 'progress'>;
  /** Its spacing: under a card's description by default. */
  className?: string;
}) {
  const progress = task.progress;
  if (!progress) {
    return null;
  }
  const total = progress.labeled + progress.inProgress + progress.left;
  if (total === 0) {
    // others labelled every item, or the task has none: nothing to measure
    return (
      <p className={classNames(className, 'text-sm text-dl-muted')}>
        Nothing to label for you
      </p>
    );
  }
  const share = Math.round((100 * progress.labeled) / total);
  return (
    <div className={className}>
      <div
        role="progressbar"
        aria-label={`Progress on ${task.name}`}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={progress.labeled}
        className="h-1.5 w-full overflow-hidden rounded-full bg-dl-track"
      >
        <div
          className={classNames(
            'h-full rounded-full',
            share >= 100 ? 'bg-dl-success' : 'bg-dl-primary'
          )}
          style={{ width: `${share}%` }}
        />
      </div>
      <p className="mt-1.5 text-sm tabular-nums text-dl-muted">
        <strong className="font-semibold text-dl-text">
          {progress.labeled}
        </strong>{' '}
        of {total} labelled
        {progress.inProgress > 0 &&
          `, ${progress.inProgress} in progress`}, {progress.left} left
      </p>
    </div>
  );
}

/**
 * Labelling or reviewing: what the user does on the task, which decides where its button leads. The colour
 * says which — DeepHealth's plum for labelling, its magenta for review — so the chip is worth reading.
 */
export function RoleChip({ reviewer }: { reviewer: boolean }) {
  return (
    <span
      className={classNames(
        'inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-sm font-semibold',
        reviewer ? 'bg-dl-review-tint text-dl-review' : 'bg-dl-tint text-dl-accent'
      )}
    >
      {reviewer ? 'Review' : 'Labeling'}
    </span>
  );
}

export function formatDay(date: string | undefined): string {
  return date
    ? new Date(date).toLocaleDateString(DATE_LOCALE, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—';
}

/**
 * The button that opens the task where the user left it: the next item to label, or the review session.
 * Solid where it is the screen's one main action; elsewhere a quiet outline that fills on hover.
 *
 * `stretched`: its clickable area covers the nearest positioned ancestor (a `relative` row or card), so the
 * whole task opens it — still one link, so keyboards and screen readers see a single control.
 */
export function TaskAction({
  task,
  variant = 'solid',
  stretched = false,
}: {
  task: Pick<LabelTask, 'taskId' | 'reviewer'>;
  variant?: 'solid' | 'outline';
  stretched?: boolean;
}) {
  return (
    <TaskLink
      task={task}
      className={classNames(
        'inline-flex h-9 items-center justify-center whitespace-nowrap rounded-lg border px-4 text-lg font-semibold transition aria-disabled:border-dl-border aria-disabled:bg-dl-sunken aria-disabled:text-dl-faint',
        variant === 'solid'
          ? 'border-dl-primary bg-dl-primary text-white hover:border-dl-primary-hover hover:bg-dl-primary-hover'
          : 'border-dl-border bg-dl-surface text-dl-text hover:border-dl-primary hover:bg-dl-primary hover:text-white group-hover:border-dl-primary group-hover:bg-dl-primary group-hover:text-white',
        { "after:absolute after:inset-0 after:content-['']": stretched }
      )}
    >
      {task.reviewer ? 'Review' : 'Open'}
    </TaskLink>
  );
}

/**
 * A task in the card grid: its role, name, what it is about, and the button to work on it — the whole card
 * opens it.
 */
export function TaskCard({ task }: { task: LabelTask }) {
  return (
    <article
      aria-label={task.name}
      className="group relative flex flex-col rounded-2xl border border-dl-border bg-dl-surface p-5 transition hover:border-dl-strong"
    >
      <div className="flex items-center justify-between gap-2">
        <RoleChip reviewer={task.reviewer} />
        <span className="font-mono text-sm text-dl-faint">#{task.taskId}</span>
      </div>
      <h3 className="mt-3 text-xl font-semibold leading-snug text-dl-text">
        {task.name}
      </h3>
      <p className="mt-1 line-clamp-3 text-lg text-dl-muted">
        {task.description || 'No description.'}
      </p>
      <TaskProgress task={task} />
      <div className="mt-auto flex items-center justify-between gap-2 pt-4">
        <span className="text-sm text-dl-faint">
          Created {formatDay(task.createdAt)}
        </span>
        {isFinished(task) ? (
          <FinishedChip />
        ) : (
          <TaskAction task={task} variant="outline" stretched />
        )}
      </div>
    </article>
  );
}

/** A task with nothing left for the user: success, in green — the only place green is used. */
export function FinishedChip() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-dl-success-tint px-3 py-1 text-sm font-semibold text-dl-success">
      ✓ Finished
    </span>
  );
}
